/* eslint-disable @typescript-eslint/no-non-null-assertion */

import LogIcon from '@material-ui/icons/AssignmentOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { capitalCase } from 'change-case';
import { EventEmitter } from 'events';
import { DateRange, isNullOrUndefined, TimeSpan, TimestampRange, UTC, utc } from 'rewire-common';
import { freeze, observable } from 'rewire-core';
import { ICell } from 'rewire-grid';
import {
  ChangeTracker,
  Form,
  field,
  IActionMenuItem,
  IEditorField,
  IFieldDefns,
  IFormContext,
  Modal,
  validator,
  validators,
} from 'rewire-ui';
import auditLogDialogModel from '../audit/AuditLogDialogModel';
import { IAuditLogFilters } from '../audit/AuditLogListModel';
import {
  DeepPartial,
  IEmployee,
  ITimeOffRange,
  ITimeOffRequest,
  TimeOffRangeInput,
  TimeOffRequestInput,
  TimeOffRequestInsertInput,
  TimeOffRequestUpdateInput,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import timeOffBalancesDialogModel from '../schedule/TimeOffBalancesDialogModel';
import timeOffDialogModel from '../timeoff/TimeOffDialogModel';
import { getAdvancedFormAutoComplete } from '../ui/AdvancedAutoComplete';
import { confirmationModel } from '../ui/ConfirmationDialog';
import { notifications } from '../ui/Notifications';
import { roundTo, timestampRangeToNumericTime } from '../utility/DataConverters';
import ProxyArrayChangeTrackerContext from '../utility/ProxyArrayChangeTrackerContext';
import { getNameSearcher } from '../utility/Searchers';
import { capitalize } from '../utility/StringUtils';
import setViewOnlyForFormFields from '../utility/setViewOnlyForFormFields';
import { timeOffRequestReturnSchema } from './TimeOffModel';

interface ITimeOffField {
  field: IEditorField;
  columns: number;
}

export interface ITimeOffDetailFields {
  startTime: ITimeOffField;
  endTime: ITimeOffField;
  units: ITimeOffField;
  reason: ITimeOffField;
}

export interface ITimeOffDetailRow {
  id: number;
  isNew: boolean;
  data: ITimeOffDetailFields;
}

let timeOffDetailRowId = 1;

const employeeSearcher = getNameSearcher('employees');
const absenceFilter = { field: 'category', in: ['absence'] };
const reasonSearcher = getNameSearcher('reasons', () => absenceFilter);

interface IInfoPanelTimeOffRequestViewModelState {
  timeOffRequests?: ITimeOffRequest[];
  currentTorIdx: number;
  allDay: boolean;
  startDate?: UTC;
  endDate?: UTC;
  units?: number;
  reason?: any;
  detailRows: ITimeOffDetailRow[];
  detailRowsMessage?: string;
  isNew: boolean;
  showTimeOffBalances: boolean;
  allowableTimeOffRequestStatusTransitions: string[];
  timeOffRequestStatusTransitionItems: IActionMenuItem[];
  canViewTimeOffBalances: boolean;
  lastSaveResult?: ITimeOffRequest[];
}

export class InfoPanelTimeOffRequestViewModel extends Modal<IInfoPanelTimeOffRequestViewModelState> {
  form: Form;
  optionsActionMenuItems: IActionMenuItem[];
  private detailRowsTracker: ChangeTracker;
  private __onEvent!: EventEmitter;

  constructor() {
    super();

    this.state.timeOffRequests = undefined;
    this.state.currentTorIdx = 0;
    this.state.allDay = true;
    this.state.detailRows = [];
    this.state.isNew = false;
    this.state.showTimeOffBalances = false;
    this.state.allowableTimeOffRequestStatusTransitions = [];
    this.state.timeOffRequestStatusTransitionItems = [];
    this.state.canViewTimeOffBalances = true;

    this.optionsActionMenuItems = [
      {
        name: 'deleteTimeOffRequest',
        title: 'Delete Time Off Request',
        icon: DeleteIcon,
        onClick: () => this.confirmDeleteTimeOffRequest(this.currentTimeOffRequest),
        closeOnClick: true,
        disabled: () =>
          timeOffDialogModel.isOpen ||
          !this.canDeleteTimeOffRequest ||
          !this.editing ||
          this.currentTimeOffRequest?.status === 'published',
      },
      {
        name: 'auditLog',
        title: 'View Audit Log',
        icon: LogIcon,
        onClick: () => this.loadAuditLogDialog(),
        closeOnClick: true,
        disabled: () => timeOffDialogModel.isOpen || !this.editing,
      },
    ];

    const timeOffRequestStatusSearcher = {
      search: async (_search?: string): Promise<string[]> => {
        const allowableStatuses = await this.getAllowableTimeOffRequestStatuses();
        return allowableStatuses;
      },
      map: (item: any) => {
        return item === undefined ? '' : capitalCase(item);
      },
    };

    this.form = Form.create(
      (form: IFormContext) => ({
        allDay: form
          .boolean()
          .label('All Day')
          .onValueChange(this.handleAllDayChange)
          .disabled(() => !this.isNew && !this.timeOffIsRequested),
        timeOffStatus: form.select(timeOffRequestStatusSearcher).label('Status').validators(validator('required')),
        employee: getAdvancedFormAutoComplete(form, employeeSearcher)
          .label('Employee')
          .placeholder('select an employee')
          .validators('required')
          .onValueChange(this.handleEmployeeChange)
          .disabled(() => !this.isNew),
        // ideally this should be fixed in rewire!! as JSX.Element | null
        startDate: form
          .date()
          .label('Start Date')
          .validators('required')
          .onValueChange(this.handleStartDateChange)
          .endAdornment(() => null as any)
          .disabled(() => !this.isNew && !this.timeOffIsRequested),
        endDate: form
          .date()
          .label('End Date')
          .validators('required', validator('>=', field('startDate')))
          .endAdornment(() => null as any)
          .disabled(() => !this.isAllDayRequest || (!this.isNew && !this.timeOffIsRequested)),
        units: form
          .number()
          .label('Units')
          .updateOnChange(true)
          .validators(validator('requiredWhenOtherIsValue', field('allDay'), true))
          .placeholder('hours requested')
          .disabled(() => !this.isNew && !this.timeOffIsRequested),
        reason: getAdvancedFormAutoComplete(form, reasonSearcher)
          .label('Reason')
          .validators(validator('requiredWhenOtherIsValue', field('allDay'), true))
          .placeholder('reason for absence')
          .onValueChange(this.handleReasonChange)
          .disabled(() => !this.isNew && !this.timeOffIsRequested),
        preference: form
          .number()
          .label('Preference')
          .validators(validator('>=', 1), validator('<=', 99))
          .disabled(() => !this.isNew && !this.timeOffIsRequested),
        comment: form
          .multistring()
          .label('Comment')
          .disabled(() => !this.isNew && !this.timeOffIsRequested),
      }),
      {
        timeOffStatus: 'Requested',
        allDay: true,
      },
      {
        variant: 'outlined',
        initialValuesValidationMode: 'none',
      },
    );

    setViewOnlyForFormFields(this.form, () => this.viewOnly);

    this.action('undo', this.undo, { disabled: () => !this.canUndo() }).action('save', this.submit, {
      type: 'submit',
      icon: 'save',
      disabled: () => !this.canSave(),
    });

    const detailRowsContext = new ProxyArrayChangeTrackerContext(this.detailRows as any[], this.detailIsComplete, 6);
    this.detailRowsTracker = new ChangeTracker(detailRowsContext);
    this.detailRowsTracker.set(this.detailRows as any[]);

    this.__onEvent = new EventEmitter();
  }

  get lastSaveResult(): ITimeOffRequest[] | undefined {
    return this.state.lastSaveResult;
  }

  detailIsComplete = (row: ITimeOffDetailRow) => {
    return (
      !isNullOrUndefined(row.data.startTime.field.value) &&
      !isNullOrUndefined(row.data.endTime.field.value) &&
      !isNullOrUndefined(row.data.units.field.value) &&
      !isNullOrUndefined(row.data.reason.field.value)
    );
  };

  get onEvent() {
    return this.__onEvent;
  }

  reset() {
    this.form.reset();
    this.removeAllDetailRows();
    this.detailRowsTracker.commit();
    freeze(() => {
      this.state.timeOffRequests = undefined;
      this.state.isNew = false;
      this.state.allowableTimeOffRequestStatusTransitions = [];
      this.state.timeOffRequestStatusTransitionItems = [];
    });
  }

  async load(timeOffRequests?: ITimeOffRequest[], isNew = false, selectedCell?: ICell) {
    this.state.enable = false;
    this.reset();
    this.state.timeOffRequests = timeOffRequests;
    this.state.isNew = isNew;
    await this.loadCurrentTimeOffRequest(selectedCell);
    if (timeOffRequests && this.canViewTimeOffBalances) {
      const timeOffRange = timeOffRequests?.flatMap((d) => d.timeOffRanges).find((d) => !!d.reason);
      await timeOffBalancesDialogModel.loadTimeOffTypes(this.employeeId, timeOffRange?.reason?.id, true);
    }
    this.state.enable = true;
  }

  async loadEmployeeRange(
    employee: IEmployee | undefined,
    start: UTC | undefined,
    end: UTC | undefined,
    units: number | undefined,
  ) {
    this.state.enable = false;
    this.reset();
    this.state.timeOffRequests = [];
    this.state.isNew = true;
    await this.loadCurrentTimeOffRequest(undefined);
    if (employee) {
      this.form.field.employee.value = employee;
    }
    if (units) {
      this.form.field.units.value = units;
    }
    if (start && end && start.date <= end.date) {
      this.form.field.startDate.value = start;
      if (end) {
        this.form.field.endDate.value = end;
      }
    }
    if (this.canViewTimeOffBalances) {
      await timeOffBalancesDialogModel.loadTimeOffTypes(this.employeeId, undefined, true);
    }
    this.state.enable = true;
  }

  loadCurrentTimeOffRequest = async (selectedCell?: ICell) => {
    this.state.allowableTimeOffRequestStatusTransitions = await this.getAllowableTimeOffRequestStatusTransitions(
      this.currentTimeOffRequest,
    );
    this.state.timeOffRequestStatusTransitionItems = this.getTimeOffRequestStatusTransitionItems(
      this.currentTimeOffRequest,
      this.allowableTimeOffRequestStatusTransitions,
    );

    this.removeAllDetailRows();
    this.loadForm(this.currentTimeOffRequest, this.isNew, selectedCell);

    if (this.currentTimeOffRequest?.partialDay) {
      this.loadDetailRows(this.currentTimeOffRequest.timeOffRanges);
    }
    if (!this.viewOnly) this.addDetailRow(); // add blank row
    this.detailRowsTracker.commit();
  };

  loadForm(timeOffRequest: ITimeOffRequest | undefined, isNew: boolean, selectedCell?: ICell) {
    // can only select initial time off status if it is a new time off request
    this.form.field.timeOffStatus.visible = isNew;

    if (!timeOffRequest) {
      let employee: any;
      let cellDate: any;
      if (selectedCell) {
        const selectedRow = selectedCell.row;
        employee = selectedRow.data?.employee;
        cellDate = selectedCell.value?.date;
      }

      cellDate = cellDate ?? UTC.now().startOfDay();
      this.form.setFieldValues({
        employee: employee,
        startDate: cellDate,
        endDate: cellDate,
      });
      return;
    }

    this.form.value = {
      allDay: !timeOffRequest.partialDay,
      timeOffStatus: timeOffRequest.status,
      employee: timeOffRequest.employee,
      startDate: timeOffRequest.effective.start,
      endDate: timeOffRequest.effective.end,
      units: !timeOffRequest.partialDay ? timeOffRequest.timeOffRanges[0].units : undefined,
      reason: !timeOffRequest.partialDay ? timeOffRequest.timeOffRanges[0].reason : undefined,
      preference: timeOffRequest.preference,
      comment: timeOffRequest.comment,
    };
  }

  loadDetailRows(timeOffRanges: ITimeOffRange[]) {
    for (const timeOffRange of timeOffRanges) {
      this.addDetailRow(timeOffRange);
    }
  }

  get timeOffRequests(): ITimeOffRequest[] | undefined {
    return this.state.timeOffRequests;
  }

  get currentTorIdx(): number {
    return !this.timeOffRequests?.length ? 0 : Math.min(this.state.currentTorIdx, this.timeOffRequests.length - 1);
  }

  set currentTorIdx(value: number) {
    this.setCurrentTorIdx(value);
  }
  async setCurrentTorIdx(value: number) {
    if (this.currentTorIdx === value) return;
    this.state.enable = false;
    this.state.currentTorIdx = Math.min(Math.max(value, 0), this.timeOffRequests?.length ? this.timeOffRequests.length - 1 : 0);
    await this.loadCurrentTimeOffRequest();
    this.state.enable = true;
  }

  get currentTimeOffRequest(): ITimeOffRequest | undefined {
    return this.timeOffRequests?.[this.currentTorIdx];
  }

  get editing(): boolean {
    return !!this.currentTimeOffRequest;
  }

  get isNew(): boolean {
    return this.state.isNew;
  }

  get canAddTimeOffRequest(): boolean {
    return mainPageModel.canAddTimeOffRequests;
  }

  get canEditTimeOffRequest(): boolean {
    return mainPageModel.canEditTimeOffRequests;
  }

  get canDeleteTimeOffRequest(): boolean {
    return mainPageModel.canDeleteTimeOffRequests;
  }

  get viewOnly(): boolean {
    return (this.isNew && !this.canAddTimeOffRequest) || (!this.isNew && !this.canEditTimeOffRequest);
  }

  get canViewTimeOffBalances(): boolean {
    return this.state.canViewTimeOffBalances && mainPageModel.canViewTimeOffBalances;
  }
  set canViewTimeOffBalances(value: boolean) {
    this.state.canViewTimeOffBalances = value;
  }

  get showTimeOffBalances(): boolean {
    return this.state.showTimeOffBalances;
  }
  set showTimeOffBalances(value: boolean) {
    this.state.showTimeOffBalances = value;
  }

  get detailRowsMessage(): string | undefined {
    return this.state.detailRowsMessage;
  }

  get isAllDayRequest(): boolean {
    return this.form.field.allDay.value;
  }

  get detailRows(): ITimeOffDetailRow[] {
    return this.state.detailRows;
  }

  set timeOffStatus(value: string) {
    this.form.field.timeOffStatus.value = value;
  }

  get timeOffStatus(): string {
    return this.form.field.timeOffStatus.value;
  }

  get allowableTimeOffRequestStatusTransitions(): string[] {
    return this.state.allowableTimeOffRequestStatusTransitions;
  }

  get timeOffRequestStatusTransitionItems(): IActionMenuItem[] {
    return this.state.timeOffRequestStatusTransitionItems;
  }

  get timeOffIsRequested(): boolean {
    return this.currentTimeOffRequest?.status === 'requested';
  }

  get employeeId(): string | undefined {
    return this.form.field.employee.value?.id;
  }

  get navigateForwardTitle() {
    return this.getNavigationText(this.currentTorIdx + 1);
  }

  get navigateBackTitle() {
    return this.getNavigationText(this.currentTorIdx - 1);
  }

  getNavigationText(index: number) {
    if (index < 0 || index >= (this.timeOffRequests?.length ?? 0)) return undefined;
    return index > this.currentTorIdx ? 'Next' : 'Previous';
  }

  canNavigateForward(): boolean {
    return this.currentTorIdx < (this.timeOffRequests?.length ?? 0) - 1;
  }

  canNavigateBack(): boolean {
    return this.currentTorIdx > 0 && (this.timeOffRequests?.length ?? 0) > 1;
  }

  navigateForward(): boolean {
    if (this.canNavigateForward()) {
      this.currentTorIdx++;
      return true;
    }
    return false;
  }

  navigateBack(): boolean {
    if (this.canNavigateBack()) {
      this.currentTorIdx--;
      return true;
    }
    return false;
  }

  async getAllowableTimeOffRequestStatuses(): Promise<string[]> {
    const timeOffRequestStatuses = await DataQueries.getPossibleTimeOffRequestStatuses();
    return timeOffRequestStatuses;
  }

  async getAllowableTimeOffRequestStatusTransitions(timeOffRequest?: ITimeOffRequest): Promise<string[]> {
    if (!timeOffRequest) {
      return [];
    }

    const result = await DataQueries.getTimeOffRequestStatus(timeOffRequest.id);
    if (result) {
      return result.allowableTransitions?.filter((at) => at.enabled).map((at) => at.event) ?? [];
    }
    return [];
  }

  getTimeOffRequestStatusTransitionItems(
    timeOffRequest: ITimeOffRequest | undefined,
    allowableTimeOffRequestStatusTransitions: string[],
  ): IActionMenuItem[] {
    if (!timeOffRequest || !allowableTimeOffRequestStatusTransitions.length) return [];

    const items: IActionMenuItem[] = [];
    for (const transition of allowableTimeOffRequestStatusTransitions) {
      items.push({
        name: transition,
        title: capitalize(transition),
        closeOnClick: true,
        onClick: () => this.executeTimeOffRequestStatusTransition(timeOffRequest, transition),
      });
    }
    return items;
  }

  handleAllDayChange = (form: Form, v?: any) => {
    if (!v) {
      this.initializePartialDay();
    }
  };

  handleEmployeeChange = async (form: Form, v?: any) => {
    await timeOffBalancesDialogModel.loadTimeOffTypes(v?.id, form.field.reason.value?.id);
  };

  handleReasonChange = async (form: Form, v?: any) => {
    await timeOffBalancesDialogModel.loadTimeOffTypes(form.field.employee.value?.id, v?.id);
  };

  removeAllDetailRows() {
    const detailRowIds = this.detailRows.map((f) => f.id);
    const detailRowFieldNames = Object.values(this.form.fields)
      .map((f) => f.name)
      .filter((fieldName) => detailRowIds.some((id) => fieldName.includes(`-${id}`)));
    this.form.removeFields(detailRowFieldNames);
    this.detailRows.set([]);
  }

  initializePartialDay() {
    this.form.field.endDate.value = this.form.field.startDate.value?.clone();
  }

  addDetailRow(timeOffRange?: ITimeOffRange) {
    const detailRow: ITimeOffDetailRow = this.createDetailRow(timeOffRange);
    detailRow.data.startTime.field.onValueChange = this.handleDetailStartTimeChangeHandler(detailRow);
    detailRow.data.endTime.field.onValueChange = this.handleDetailEndTimeChangeHandler(detailRow);
    detailRow.data.units.field.onValueChange = this.handleDetailUnitsChangeHandler(detailRow);
    detailRow.data.reason.field.onValueChange = this.handleDetailReasonChangeHandler(detailRow);
    this.detailRows.push(detailRow);
  }

  removeTimeOffDetail(id: number) {
    const index = this.detailRows.findIndex((r) => r.id === id);
    if (index !== -1) {
      this.detailRows.splice(index, 1);

      const detailRowFieldNames = Object.values(this.form.fields)
        .map((x) => x.name)
        .filter((x) => x.includes(`-${id}`));
      this.form.removeFields(detailRowFieldNames);
      this.detailRowsTracker.recalculate();
    }
  }

  evaluateForNewDetailRow(row: ITimeOffDetailRow) {
    this.state.detailRowsMessage = undefined;
    if (this.isNewDetailRowRequired(row)) {
      row.isNew = false;
      this.addDetailRow();
    }
  }

  isNewDetailRowRequired(row: ITimeOffDetailRow): boolean {
    if (!row.isNew) return false;
    if (isNullOrUndefined(row.data.startTime.field.value)) return false;
    if (isNullOrUndefined(row.data.endTime.field.value)) return false;
    if (isNullOrUndefined(row.data.units.field.value)) return false;
    if (isNullOrUndefined(row.data.reason.field.value)) return false;
    return true;
  }

  createDetailRow(timeOffRange?: ITimeOffRange): ITimeOffDetailRow {
    const id: number = timeOffDetailRowId++;
    const times = timestampRangeToNumericTime(timeOffRange?.effective, this.currentTimeOffRequest?.effective.start);

    const newFormFieldNames = [`startTime-${id}`, `endTime-${id}`, `units-${id}`, `reason-${id}`];

    this.form.addFields(
      (_) => {
        const fields: IFieldDefns = {};
        const startField = _.time()
          .label('Start Time')
          .disabled(() => !this.isNew && !this.timeOffIsRequested);
        fields[newFormFieldNames[0]] = startField;
        const endField = _.time()
          .label('End Time')
          .disabled(() => !this.isNew && !this.timeOffIsRequested);
        fields[newFormFieldNames[1]] = endField;
        const unitsField = _.number({ decimals: 2, thousandSeparator: false, allowNegative: false })
          .label('Units')
          .disabled(() => !this.isNew && !this.timeOffIsRequested);
        fields[newFormFieldNames[2]] = unitsField;
        const reasonField = getAdvancedFormAutoComplete(_, reasonSearcher)
          .label('Reason')
          .validators('required')
          .disabled(() => !this.isNew && !this.timeOffIsRequested);
        fields[newFormFieldNames[3]] = reasonField;

        return fields;
      },
      {
        ...this.form.toObject(),
        [newFormFieldNames[0]]: times?.start,
        [newFormFieldNames[1]]: times?.end,
        [newFormFieldNames[2]]: timeOffRange?.units,
        [newFormFieldNames[3]]: timeOffRange?.reason,
      },
    );

    setViewOnlyForFormFields(
      this.form,
      () => this.viewOnly,
      this.form.fields.map((f) => f.name).filter((name) => !newFormFieldNames.includes(name)),
    );

    const startField = this.form.field[newFormFieldNames[0]];
    const endField = this.form.field[newFormFieldNames[1]];
    const unitsField = this.form.field[newFormFieldNames[2]];
    const reasonField = this.form.field[newFormFieldNames[3]];

    const row: ITimeOffDetailRow = observable({
      id,
      isNew: !timeOffRange,
      data: {
        startTime: { field: startField, columns: 1 },
        endTime: { field: endField, columns: 1 },
        units: { field: unitsField, columns: 1 },
        reason: { field: reasonField, columns: 2 },
      },
    });

    const startValidator = validators(
      validator('requiredWhenAnyOthersAreNotNull', field(endField.name), field(unitsField.name), field(reasonField.name)),
    );
    const endValidator = validators(
      validator('requiredWhenAnyOthersAreNotNull', field(startField.name), field(unitsField.name), field(reasonField.name)),
    );
    const unitsValidator = validators(
      validator('requiredWhenAnyOthersAreNotNull', field(startField.name), field(endField.name), field(reasonField.name)),
    );
    const reasonValidator = validators(
      validator('requiredWhenAnyOthersAreNotNull', field(startField.name), field(endField.name), field(unitsField.name)),
    );

    this.form.validator.setRule(startField.name, startValidator);
    this.form.validator.setRule(endField.name, endValidator);
    this.form.validator.setRule(unitsField.name, unitsValidator);
    this.form.validator.setRule(reasonField.name, reasonValidator);

    return row;
  }

  handleStartDateChange = (form: Form, v?: UTC) => {
    if (!this.isAllDayRequest || !this.form.field.endDate.value) {
      this.form.field.endDate.value = v;
    }
  };

  handleDetailStartTimeChangeHandler = (row: ITimeOffDetailRow) => (_form: Form, _value: any) => {
    if (!isNullOrUndefined(row.data.startTime.field.value)) {
      if (!isNullOrUndefined(row.data.endTime.field.value)) {
        row.data.units.field.value = Math.max(roundTo(row.data.endTime.field.value - row.data.startTime.field.value, 5), 0);
      } else if (!isNullOrUndefined(row.data.units.field.value)) {
        row.data.endTime.field.value = roundTo(row.data.startTime.field.value + row.data.units.field.value, 5);
      }
    }
    this.evaluateForNewDetailRow(row);
    this.detailRowsTracker.recalculate();
  };

  handleDetailEndTimeChangeHandler = (row: ITimeOffDetailRow) => (_form: Form, _value: any) => {
    if (!isNullOrUndefined(row.data.endTime.field.value)) {
      if (!isNullOrUndefined(row.data.startTime.field.value)) {
        row.data.units.field.value = Math.max(roundTo(row.data.endTime.field.value - row.data.startTime.field.value, 5), 0);
      } else if (!isNullOrUndefined(row.data.units.field.value)) {
        row.data.startTime.field.value = roundTo(row.data.endTime.field.value - row.data.units.field.value, 5);
      }
    }
    this.evaluateForNewDetailRow(row);
    this.detailRowsTracker.recalculate();
  };

  handleDetailUnitsChangeHandler = (row: ITimeOffDetailRow) => (_form: Form, _value: any) => {
    if (!isNullOrUndefined(row.data.units.field.value)) {
      if (!isNullOrUndefined(row.data.startTime.field.value)) {
        row.data.endTime.field.value = roundTo(row.data.startTime.field.value + row.data.units.field.value, 5);
      } else if (!isNullOrUndefined(row.data.endTime.field.value)) {
        row.data.startTime.field.value = roundTo(row.data.endTime.field.value - row.data.units.field.value, 5);
      }
    }
    this.evaluateForNewDetailRow(row);
    this.detailRowsTracker.recalculate();
  };

  handleDetailReasonChangeHandler = (row: ITimeOffDetailRow) => (_form: Form, _value: any) => {
    this.evaluateForNewDetailRow(row);
    this.detailRowsTracker.recalculate();
  };

  shouldShowPanel(): boolean {
    return this.editing || this.isNew;
  }

  isNewRow(detailRow: ITimeOffDetailRow): boolean {
    return detailRow.isNew;
  }

  hasChanges(): boolean {
    return this.form.hasChanges || (!this.isAllDayRequest && this.detailRowsTracker.hasChanges);
  }

  async executeTimeOffRequestStatusTransition(timeOffRequest: ITimeOffRequest, newStatus: string) {
    if (!this.canEditTimeOffRequest) return;
    const result = await DataMutations.transitionTimeOffRequest(timeOffRequest.id, newStatus);
    if (result) {
      timeOffRequest.status = result.status;
      this.onEvent.emit('updateStatus', result.id, result.status);
      this.onEvent.emit('submit', [timeOffRequest]);
      notifications.success(`Time Off Request ${result.status}`);
    } else {
      notifications.error('Time Off Request Status Transition Failed');
    }
  }

  deleteTimeOffRequest = async (timeOffRequest?: ITimeOffRequest) => {
    if (!this.canDeleteTimeOffRequest || !this.editing || !timeOffRequest || timeOffRequest?.status === 'published') return;
    const result = await DataMutations.deleteTimeOffRequests([timeOffRequest.id]);
    if (result) {
      this.onEvent.emit('delete', [timeOffRequest.id]);
      notifications.success('Time Off Request Deleted');
    } else {
      notifications.error('Time Off Request Deletion Failed');
    }
  };

  deleteTimeOffRequests = async (timeOffIds: string[]) => {
    if (!this.canDeleteTimeOffRequest) return;
    const isMultiple = timeOffIds.length > 1;
    const timeOffWord = `Time Off Request${isMultiple ? 's' : ''}`;
    const result = await DataMutations.deleteTimeOffRequests(timeOffIds);
    if (result) {
      this.onEvent.emit('delete', timeOffIds);
      notifications.success(`${timeOffWord} Deleted`);
    } else {
      notifications.error(`${timeOffWord} Deletion Failed`);
    }
  };

  confirmDeleteTimeOffRequest(timeOffRequest?: ITimeOffRequest) {
    if (!this.canDeleteTimeOffRequest || !this.editing || !timeOffRequest || timeOffRequest.status === 'published') return;
    const message = 'Are you sure you want to delete this time off request?';
    let secondaryMessage: string | undefined;
    if (timeOffRequest.status === 'approved') {
      secondaryMessage =
        'Removing a time off request of status "approved" will not automatically revert the absences that it has already created. To do so, first set the time off request status back to "requested" before deletion.';
    }
    confirmationModel.load(message, {
      yesAction: () => {
        this.deleteTimeOffRequest(timeOffRequest);
        return true;
      },
      maxWidth: 'sm',
      title: 'Confirm Time Off Request Deletion',
      secondaryMessage: secondaryMessage,
    });
  }

  confirmDeleteTimeOffRequests(timeOffRequests: ITimeOffRequest[]) {
    if (!this.canDeleteTimeOffRequest) return;
    const isMultiple = timeOffRequests.length > 1;
    const message = `Are you sure you want to delete ${isMultiple ? 'these time off requests' : 'this time off request'}?`;
    let secondaryMessage: string = '';
    if (timeOffRequests.some((t) => t.status === 'approved')) {
      secondaryMessage =
        'Removing a time off request of status "approved" will not automatically revert the absences that it has already created. To do so, first set the time off request status back to "requested" before deletion.';
    }
    confirmationModel.load(message, {
      yesAction: () => {
        this.deleteTimeOffRequests(timeOffRequests.map((t) => t.id));
        return true;
      },
      maxWidth: 'sm',
      title: `Confirm Time Off Request${isMultiple ? 's' : ''} Deletion`,
      secondaryMessage: secondaryMessage,
    });
  }

  loadAuditLogDialog() {
    const filter = { types: ['timeoff'] } as IAuditLogFilters;
    if (this.currentTimeOffRequest?.id) {
      filter.entityIds = [this.currentTimeOffRequest?.id];
    }
    auditLogDialogModel.load(filter);
  }

  private extractInputFromForm(): TimeOffRequestInput {
    try {
      const startDate = utc(this.form.field.startDate.value).clone();
      const endDate = utc(this.form.field.endDate.value).clone();

      const input: TimeOffRequestInput = {
        employeeId: this.form.field.employee.value.id,
        effective: new DateRange(startDate, endDate),
        partialDay: !this.form.field.allDay.value,
        comment: this.form.field.comment.value || '',
        timeOffRanges: [] as TimeOffRangeInput[],
      };

      if (this.form.field.preference.value !== undefined) {
        input.preference = this.form.field.preference.value;
      }

      if (input.partialDay) {
        for (const detail of this.detailRows) {
          if (detail.isNew) {
            continue;
          }
          const startTime = utc(startDate).clone().add(detail.data.startTime.field.value, TimeSpan.hours, 4);
          const endTime = utc(startTime).clone().add(detail.data.units.field.value, TimeSpan.hours, 4);
          const timeOffRange = {
            effective: new TimestampRange(startTime, endTime),
            units: detail.data.units.field.value,
            reasonId: detail.data.reason.field.value.id,
          };
          input.timeOffRanges.push(timeOffRange);
        }
      } else {
        input.timeOffRanges.push({
          units: this.form.field.units.value,
          reasonId: this.form.field.reason.value.id,
        });
      }
      return input;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  canUndo(): boolean {
    return !this.viewOnly && this.hasChanges();
  }

  undo = () => {
    if (this.canUndo()) {
      this.load(this.timeOffRequests);
    }
    return false;
  };

  canSave(): boolean {
    return !this.viewOnly && ((this.editing && this.hasChanges()) || this.isNew) && !!this.employeeId;
  }

  private async _save(timeOffRequestInput: TimeOffRequestInput) {
    let result: DeepPartial<ITimeOffRequest>[] | undefined;
    if (this.editing) {
      const timeOffRequestUpdateInput = timeOffRequestInput as TimeOffRequestUpdateInput;
      timeOffRequestUpdateInput.id = this.currentTimeOffRequest!.id;
      result = await DataMutations.updateTimeOffRequests(timeOffRequestReturnSchema, [timeOffRequestUpdateInput]);
    } else {
      const timeOffRequestInsertInput = timeOffRequestInput as TimeOffRequestInsertInput;
      timeOffRequestInsertInput.status = this.form.field.timeOffStatus.value.toLocaleLowerCase();
      result = await DataMutations.insertTimeOffRequests(timeOffRequestReturnSchema, [timeOffRequestInsertInput]);
    }

    if (result?.length) {
      this.onEvent.emit('save', result);
      this.state.lastSaveResult = result as ITimeOffRequest[];
      notifications.success(`Time Off Request ${this.editing ? 'Updated' : 'Created'}`);
      if (this.isNew) {
        this.load();
      }
      return true;
    }
    notifications.error(`Time Off Request ${this.editing ? 'Update' : 'Creation'} Failed`);
    return false;
  }

  private save = async (): Promise<boolean> => {
    this.state.lastSaveResult = undefined;
    if (!this.canSave()) {
      return false;
    }

    const timeOffRequestInput = this.extractInputFromForm();
    if (timeOffRequestInput.timeOffRanges.length === 0) {
      this.state.detailRowsMessage = 'At least one Detail Row must be created';
      return false;
    }

    if (timeOffRequestInput.effective.start >= UTC.now().startOfDay()) {
      return await this._save(timeOffRequestInput);
    }
    return new Promise((resolve) => {
      confirmationModel.load(`Are you sure you want to ${this.editing ? 'update' : 'create'} a backdated time off request?`, {
        yesAction: async () => {
          const result = await this._save(timeOffRequestInput);
          resolve(result);
          return true;
        },
        noAction: () => {
          resolve(false);
          return true;
        },
        maxWidth: 'sm',
        title: `Confirm Time Off Request ${this.editing ? 'Update' : 'Creation'}`,
      });
    });
  };

  submit = async () => {
    if (this.viewOnly) return false;
    await this.form.submitAsync(this.save);
    if (this.state.lastSaveResult !== undefined) {
      this.onEvent.emit('submit', this.state.lastSaveResult);
    }
    return false;
  };
}

const infoPanelTimeOffRequestViewModel = new InfoPanelTimeOffRequestViewModel();
export const dialogInfoPanelTimeOffRequestViewModel = new InfoPanelTimeOffRequestViewModel();
export default infoPanelTimeOffRequestViewModel;
